/** biome-ignore-all lint/style/noNonNullAssertion: since we are using in-memory event store for testing */
import type {
  AggregateStreamOptions,
  AggregateStreamResult,
  AppendToStreamOptions,
  AppendToStreamResult,
  Event,
  EventMetadataOf,
  EventStore,
  ExpectedStreamVersion,
  QueryEventsOptions,
  QueryEventsResult,
  ReadEvent,
  ReadStreamOptions,
  ReadStreamResult,
  StreamId,
  StreamPosition,
} from '../core/types.js';

/**
 * Stored event with system fields (internal format)
 */
type StoredEvent = {
  event: Event;
  streamId: string;
  streamPosition: StreamPosition;
  globalPosition: number;
  eventId: string;
  schemaVersion: string;
  transactionId: string;
  createdAt: string;
};

/**
 * Stream metadata for internal tracking
 */
type InternalStreamMetadata = {
  streamId: string;
  version: StreamPosition;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown>;
};

/**
 * In-memory implementation of EventStore for testing and development
 *
 * This implementation provides a complete event store that runs entirely in memory,
 * making it perfect for unit tests, integration tests, and development scenarios
 * where you don't need persistence.
 */
export class InMemoryEventStore implements EventStore {
  private streams = new Map<string, StoredEvent[]>();
  private streamMetadata = new Map<string, InternalStreamMetadata>();
  private globalPositionCounter = 0;

  /**
   * Append events to a stream
   *
   * @param streamId - The stream identifier
   * @param events - Events to append
   * @param options - Append options including expected version
   * @returns Promise resolving to the append result
   */
  async appendToStream<EventType extends Event = Event>(
    streamId: StreamId,
    events: EventType[],
    options?: AppendToStreamOptions
  ): Promise<AppendToStreamResult> {
    if (!streamId) {
      throw new Error('Stream id is required');
    }

    if (!Array.isArray(events) || events.length === 0) {
      throw new Error('Events array cannot be empty');
    }

    const existingEvents = this.streams.get(streamId) || [];
    const currentVersion = existingEvents.length;
    const streamExists = this.streams.has(streamId);

    // Check expected version for optimistic concurrency control
    if (options?.expectedStreamVersion !== undefined) {
      this.assertExpectedVersionMatchesCurrent(
        currentVersion,
        options.expectedStreamVersion,
        streamExists
      );
    }

    // Convert events to stored format with system fields
    const now = new Date().toISOString();
    const storedEvents: StoredEvent[] = events.map((event, index) => ({
      event,
      streamId,
      streamPosition: currentVersion + index + 1,
      globalPosition: ++this.globalPositionCounter,
      eventId: crypto.randomUUID(),
      schemaVersion:
        'metadata' in event &&
        typeof event.metadata === 'object' &&
        event.metadata !== null
          ? 'schemaVersion' in event.metadata
            ? String(event.metadata.schemaVersion)
            : '1.0.0'
          : '1.0.0',
      transactionId:
        'metadata' in event &&
        typeof event.metadata === 'object' &&
        event.metadata !== null
          ? 'transactionId' in event.metadata && event.metadata.transactionId
            ? String(event.metadata.transactionId)
            : crypto.randomUUID()
          : crypto.randomUUID(),
      metadata: 'metadata' in event ? event.metadata || {} : {},
      createdAt: now,
    }));

    // Append events
    const newEvents = [...existingEvents, ...storedEvents];
    this.streams.set(streamId, newEvents);

    // Update stream metadata
    const existing = this.streamMetadata.get(streamId);
    this.streamMetadata.set(streamId, {
      streamId,
      version: newEvents.length,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      metadata: { ...existing?.metadata },
    });

    return {
      nextExpectedStreamVersion: newEvents.length,
      createdNewStream: !streamExists,
    };
  }

  /**
   * Helper method to assert expected version matches current version
   * @private
   */
  private assertExpectedVersionMatchesCurrent(
    currentVersion: StreamPosition,
    expectedVersion: ExpectedStreamVersion<StreamPosition>,
    streamExists: boolean
  ): void {
    if (typeof expectedVersion === 'number') {
      if (expectedVersion !== currentVersion) {
        throw new Error(
          `Expected stream version ${expectedVersion} but current version is ${currentVersion}`
        );
      }
    } else if (expectedVersion === 'no_stream' && streamExists) {
      throw new Error('Expected no stream but stream exists');
    } else if (expectedVersion === 'stream_exists' && !streamExists) {
      throw new Error('Expected stream to exist but it does not');
    }
    // 'any' version means no check needed
  }

  /**
   * Get all stream IDs (utility method for testing)
   *
   * @returns Array of all stream identifiers
   * @public
   */
  getAllStreamIds(): StreamId[] {
    return Array.from(this.streams.keys());
  }

  /**
   * Get total event count across all streams (utility method for testing)
   *
   * @returns Total number of events stored
   * @public
   */
  getTotalEventCount(): number {
    return Array.from(this.streams.values()).reduce(
      (total, events) => total + events.length,
      0
    );
  }

  /**
   * Clear all data (utility method for testing)
   * @public
   */
  clear(): void {
    this.streams.clear();
    this.streamMetadata.clear();
    this.globalPositionCounter = 0;
  }

  /**
   * Get all events across all streams (utility method for testing)
   *
   * @returns Array of all events ordered by global position
   * @public
   */
  getAllEvents(): ReadEvent[] {
    const allEvents: StoredEvent[] = [];

    for (const events of this.streams.values()) {
      allEvents.push(...events);
    }

    const sortedEvents = allEvents.sort(
      (a, b) => a.globalPosition - b.globalPosition
    );

    // Convert to ReadEvent format
    return sortedEvents.map(
      (stored): ReadEvent => ({
        type: stored.event.type,
        data: stored.event.data,
        metadata:
          'metadata' in stored.event
            ? (stored.event.metadata as EventMetadataOf<Event>)
            : undefined,
        streamId: stored.streamId,
        streamPosition: stored.streamPosition,
        globalPosition: stored.globalPosition,
        eventId: stored.eventId,
        schemaVersion: stored.schemaVersion,
        transactionId: stored.transactionId,
        createdAt: stored.createdAt,
      })
    );
  }

  /**
   * Check if a stream exists (utility method for testing)
   *
   * @param streamId - The stream identifier
   * @returns Promise resolving to boolean indicating existence
   * @public
   */
  async streamExists(streamId: StreamId): Promise<boolean> {
    return this.streams.has(streamId);
  }

  /**
   * Aggregate events from a stream to rebuild state
   *
   * @param streamId - The stream identifier
   * @param options - Aggregation options including initial state and evolution function
   * @returns Promise resolving to the aggregated state and stream metadata
   */
  async aggregateStream<State, EventType extends Event>(
    streamId: StreamId,
    options: AggregateStreamOptions<State, EventType>
  ): Promise<AggregateStreamResult<State>> {
    // Check if stream exists
    const streamExists = this.streams.has(streamId);

    // Read events from the stream using the read options
    const readResult = await this.readStream<EventType>(streamId, options.read);

    // Start with initial state
    let state = options.initialState();

    // Apply each event to evolve the state
    for (const event of readResult.events) {
      state = options.evolve(state, event);
    }

    return {
      currentStreamVersion: readResult.currentStreamVersion,
      state,
      streamExists,
    };
  }

  /**
   * Read events from a stream with options
   *
   * @param streamId - The stream identifier
   * @param options - Read options for filtering and pagination
   * @returns Promise resolving to read result with events and metadata
   */
  async readStream<EventType extends Event>(
    streamId: StreamId,
    options?: ReadStreamOptions
  ): Promise<ReadStreamResult<EventType>> {
    if (!streamId) {
      throw new Error('Stream id is required');
    }

    const storedEvents = this.streams.get(streamId) || [];
    const streamExists = this.streams.has(streamId);
    const currentStreamVersion = storedEvents.length;

    // Handle expectedStreamVersion check first
    if (options?.expectedStreamVersion !== undefined) {
      this.assertExpectedVersionMatchesCurrent(
        currentStreamVersion,
        options.expectedStreamVersion,
        streamExists
      );
    }

    // If stream doesn't exist, return empty result
    if (!streamExists) {
      return {
        currentStreamVersion: 0,
        events: [],
        streamExists: false,
      };
    }

    let filteredEvents = [...storedEvents];

    // Apply filtering based on options
    if (options) {
      // Handle 'from' option
      if ('from' in options && options.from !== undefined) {
        const fromIndex = Math.max(0, Number(options.from) - 1); // Convert to 0-based index
        filteredEvents = filteredEvents.slice(fromIndex);
      }

      // Handle 'to' option
      if ('to' in options && options.to !== undefined) {
        const fromIndex =
          'from' in options && options.from !== undefined
            ? Math.max(0, Number(options.from) - 1)
            : 0;
        const toIndex = Math.min(storedEvents.length, Number(options.to));
        filteredEvents = storedEvents.slice(fromIndex, toIndex);
      }

      // Handle 'maxCount' option
      if (
        'maxCount' in options &&
        options.maxCount !== undefined &&
        options.maxCount > 0
      ) {
        filteredEvents = filteredEvents.slice(0, options.maxCount);
      }
    }

    // Convert to ReadEvent format
    const events = filteredEvents.map((stored) => ({
      type: stored.event.type,
      data: stored.event.data,
      metadata:
        'metadata' in stored.event
          ? (stored.event.metadata as EventMetadataOf<EventType>)
          : undefined,
      streamId: stored.streamId,
      streamPosition: stored.streamPosition,
      globalPosition: stored.globalPosition,
      eventId: stored.eventId,
      schemaVersion: stored.schemaVersion,
      transactionId: stored.transactionId,
      createdAt: stored.createdAt,
    })) as ReadEvent<EventType>[];

    return {
      currentStreamVersion,
      events,
      streamExists,
    };
  }

  /**
   * Query events across all streams with flexible filtering options
   *
   * @param options - Query parameters for filtering events
   * @returns Promise resolving to the query result with events and pagination info
   */
  async queryEvents<EventType extends Event>(
    options: QueryEventsOptions = {}
  ): Promise<QueryEventsResult<EventType>> {
    // Get all events across all streams
    let allEvents = this.getAllEvents() as ReadEvent<EventType>[];

    // Apply filters
    if (options.streamId) {
      allEvents = allEvents.filter(
        (event) => event.streamId === options.streamId
      );
    }

    if (options.eventId) {
      allEvents = allEvents.filter(
        (event) => event.eventId === options.eventId
      );
    }

    if (options.type) {
      if (Array.isArray(options.type)) {
        allEvents = allEvents.filter((event) =>
          options.type!.includes(event.type)
        );
      } else {
        allEvents = allEvents.filter((event) => event.type === options.type);
      }
    }

    if (options.transactionId) {
      allEvents = allEvents.filter(
        (event) => event.transactionId === options.transactionId
      );
    }

    if (options.fromPosition !== undefined) {
      allEvents = allEvents.filter(
        (event) => event.globalPosition >= options.fromPosition!
      );
    }

    if (options.toPosition !== undefined) {
      allEvents = allEvents.filter(
        (event) => event.globalPosition <= options.toPosition!
      );
    }

    if (options.fromDate) {
      allEvents = allEvents.filter(
        (event) => event.createdAt >= options.fromDate!
      );
    }

    if (options.toDate) {
      allEvents = allEvents.filter(
        (event) => event.createdAt <= options.toDate!
      );
    }

    // Note: phase and includeArchived are not supported in the in-memory implementation
    // as these are platform-specific concepts

    // Apply sorting
    const sortBy = options.sortBy || 'globalPosition';
    const sortDirection = options.sortDirection || 'asc';

    allEvents.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'globalPosition':
          comparison = a.globalPosition - b.globalPosition;
          break;
        case 'streamPosition':
          comparison = a.streamPosition - b.streamPosition;
          break;
        case 'createdAt':
          comparison = a.createdAt.localeCompare(b.createdAt);
          break;
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });

    // Count total before pagination
    const total = allEvents.length;

    // Apply pagination
    const limit = Math.min(options.limit || 100, 1000); // Cap at 1000 max
    const offset = options.offset || 0;
    const paginatedEvents = allEvents.slice(offset, offset + limit);

    return {
      events: paginatedEvents,
      pagination: {
        limit,
        offset,
        total: options.includeCount ? total : undefined,
        hasMore: offset + limit < total,
      },
    };
  }
}
